import { IconBuildings, IconQuestionMark } from '@tabler/icons-react';

import { Avatar, AvatarGroup, Group, Stack, Text, Tooltip } from '@optiro/ui';

import { AccountAvatar } from '@/components/AccountAvatar';
import { AccountCategoryBadge } from '@/components/AccountCategoryBadge/AccountCategoryBadge';
import { ResourceWithCategory } from '@/components/ResourceWithCategory/ResourceWithCategory';
import { useAccounts } from '@/hooks/useAccounts';
import { usePartners } from '@/hooks/usePartners/usePartners';
import { useResources } from '@/hooks/useResources';
import { Task } from '@/types/types';

export const Resources = ({ taskId }: { taskId: Task['id'] }) => {
  const { data: allResources = [] } = useResources({ taskId });
  const { data: accounts } = useAccounts();
  const { data: partners } = usePartners();

  if (!allResources || allResources.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Ingen ressourcer
      </Text>
    );
  }

  return (
    <AvatarGroup spacing="sm">
      {allResources.map((resource) => {
        if (resource.resourceable_type === 'Account') {
          const account = accounts?.find((a) => a.id === resource.resourceable_id);
          return (
            <Tooltip label={account?.name || 'Loading...'} key={resource.id}>
              <AccountAvatar
                photoUrl={account?.photo?.thumb?.url}
                name={account?.name ?? ''}
                radius="xl"
                size="sm"
              />
            </Tooltip>
          );
        }

        if (resource.resourceable_type === 'Partner') {
          const partner = partners?.find((p) => p.id === resource.resourceable_id);
          return (
            <Tooltip label={partner?.name || 'Loading...'} key={resource.id}>
              <Avatar color="orange" radius="xl" size="sm">
                <IconBuildings size={20} stroke={1.5} />
              </Avatar>
            </Tooltip>
          );
        }

        // ResourcePlaceholder
        return (
          <Tooltip label="Pladsholder" key={resource.id}>
            <Avatar color="gray" radius="xl" size="sm">
              <IconQuestionMark size={20} stroke={1.5} />
            </Avatar>
          </Tooltip>
        );
      })}
    </AvatarGroup>
  );
};

export const ResourcesStack = ({ taskId }: { taskId: Task['id'] }) => {
  const { data: allResources = [] } = useResources({ taskId });

  if (!allResources || allResources.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Ingen ressourcer
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      {allResources.map((resource) => (
        <Group
          key={resource.id}
          justify="space-between"
          p="xs"
          style={{
            borderRadius: '6px',
            backgroundColor: '#f8f9fa',
          }}
        >
          <Group gap="sm">
            <ResourceWithCategory
              resourceId={resource.id}
              resourceableId={resource.resourceable_id}
              resourceableType={resource.resourceable_type}
              size="sm"
              category={resource.name}
            />
          </Group>
          <AccountCategoryBadge size="sm" categoryId={resource.company_account_category_id} />
        </Group>
      ))}
    </Stack>
  );
};

export default Resources;
